import React from "react";
import {
  Box,
  Typography,
  Container,
  Paper,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemText,
  Divider,
  Grid,
  Button,
} from "@mui/material";
import {
  DirectionsBus,
  Security,
  Shield,
  Lock,
  Visibility,
  ContactMail,
  Business,
  Update,
  ArrowBack,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import WhiteLogo from "../../assets/logos/gaadikharcha-logo-white.png";
import { useNavigate } from "react-router-dom";

const Section = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 2),
  backgroundColor: "#fff",
}));

const PolicySection = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(4),
  marginBottom: theme.spacing(3),
  borderRadius: theme.spacing(2),
  border: "1px solid #e2e8f0",
  backgroundColor: "#ffffff",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
  },
}));

const PrivacyPolicy: React.FC = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: "#f8fafc" }}>
      {/* Header */}
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: "#1a365d",
          borderBottom: "1px solid #2d4a69",
          borderRadius: 0,
          zIndex: 1200,
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            minHeight: "72px",
          }}
        >
          {/* Left side - GaadiKharcha.com with menu icon */}
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <img
              src={WhiteLogo}
              alt="GaadiKharcha"
              style={{ width: "120px", height: "auto" }}
            />
          </Box>
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Button
              variant="text"
              color="inherit"
              onClick={() => navigate("/")}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                textTransform: "none",
              }}
            >
              <ArrowBack />
              Back to Home
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1a365d 0%, #2563eb 100%)",
          color: "#ffffff",
          py: { xs: 4, md: 6 },
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Background Pattern */}
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `
              linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
              linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)
            `,
            backgroundSize: "20px 20px",
            opacity: 0.3,
          }}
        />

        <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
          <Box sx={{ textAlign: "center" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
              <Shield
                sx={{
                  fontSize: 48,
                  color: "#60a5fa",
                  filter: "drop-shadow(0 4px 8px rgba(0,0,0,0.2))",
                }}
              />
            </Box>
            <Typography
              variant="h2"
              fontWeight={700}
              gutterBottom
              sx={{
                fontSize: { xs: "1.75rem", md: "2rem" },
                mb: 0.5,
                textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Privacy Policy
            </Typography>
            <Typography
              variant="h6"
              sx={{
                maxWidth: 400,
                mx: "auto",
                mb: 0.5,
                opacity: 0.9,
                lineHeight: 1.3,
                fontSize: "1rem",
              }}
            >
              Your privacy and data security are our top priorities
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Main Content */}
      <Section>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Table of Contents */}
            {/* <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 3,
                  backgroundColor: "#ffffff",
                  border: "1px solid #e2e8f0",
                  position: "sticky",
                  top: 20,
                }}
              >
                <Typography
                  variant="h6"
                  fontWeight={700}
                  color="#1a365d"
                  gutterBottom
                >
                  Table of Contents
                </Typography>
                <List dense>
                  {[
                    "Information We Collect",
                    "How We Use Information",
                    "Information Sharing",
                    "Data Security",
                    "Data Retention",
                    "Your Rights",
                    "Cookies Policy",
                    "Updates to Policy",
                    "Contact Information",
                  ].map((item, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        py: 0.5,
                        px: 0,
                        cursor: "pointer",
                        borderRadius: 1,
                        "&:hover": {
                          backgroundColor: "#f1f5f9",
                        },
                      }}
                    >
                      <ListItemText
                        primary={item}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "0.875rem",
                            color: "#64748b",
                            fontWeight: 500,
                          },
                        }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid> */}

            {/* Policy Content */}
            <Grid item xs={12} md={12}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                {/* Introduction */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Business sx={{ color: "#60a5fa", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      Introduction
                    </Typography>
                  </Box>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    Gaadikarcha.com: An Aequitous Platform
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    At Gaadikarcha.com, we value your privacy. This Privacy
                    Policy outlines how we collect, use, store, and protect your
                    data when you use our platform.
                  </Typography>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8 }}
                  >
                    By using our services, you agree to the terms of this
                    Privacy Policy.
                  </Typography>
                </PolicySection>

                {/* Information We Collect */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Visibility
                      sx={{ color: "#10b981", mr: 2, fontSize: 24 }}
                    />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      1. Information We Collect
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    We collect the following types of data:
                  </Typography>

                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="#1a365d"
                    gutterBottom
                  >
                    a. User Information
                  </Typography>
                  <List sx={{ mb: 3 }}>
                    {[
                      "Name, phone number, email address",
                      "Business name and type (e.g., transport operator)",
                      "Location and contact details",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="#1a365d"
                    gutterBottom
                  >
                    b. Vehicle and Fleet Data
                  </Typography>
                  <List sx={{ mb: 3 }}>
                    {[
                      "Vehicle registration numbers, insurance details, trip logs",
                      "Loan details, maintenance records, tax information",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Typography
                    variant="h6"
                    fontWeight={600}
                    color="#1a365d"
                    gutterBottom
                  >
                    c. Usage Data
                  </Typography>
                  <List>
                    {[
                      "Login times, activity within the app",
                      "IP address, device/browser type",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </PolicySection>

                {/* How We Use Information */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Business sx={{ color: "#f59e0b", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      2. How We Use Your Data
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    We use the data you provide for the following purposes:
                  </Typography>

                  <List>
                    {[
                      "To offer and improve our platform services",
                      "To track vehicle-level income, expenses, and alerts",
                      "To generate reports and summaries for your business",
                      "To notify you of updates, reminders, or critical actions",
                      "To provide customer support and resolve issues",
                      "To comply with legal obligations (if any)",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mt: 2 }}
                  >
                    We do not sell, rent, or trade your data to third parties.
                  </Typography>
                </PolicySection>

                {/* Data Security */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Security sx={{ color: "#8b5cf6", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      3. Data Storage & Security
                    </Typography>
                  </Box>

                  <List>
                    {[
                      "All data is stored securely on trusted cloud servers",
                      "We use encryption, firewalls, and regular security audits",
                      "Only authorized personnel have access to your data",
                      "You are responsible for safeguarding your login credentials",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </PolicySection>

                {/* Data Sharing */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Shield sx={{ color: "#ef4444", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      4. Data Sharing
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    We may share your data only in the following situations:
                  </Typography>

                  <List>
                    {[
                      "With your explicit permission (e.g., sharing reports with your CA)",
                      "With government authorities if legally required",
                      "With trusted service providers helping us run the platform (e.g., hosting, analytics), under strict confidentiality",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </PolicySection>

                {/* Cookies & Tracking */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Lock sx={{ color: "#60a5fa", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      5. Cookies & Tracking
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    We use cookies to:
                  </Typography>

                  <List>
                    {[
                      "Improve user experience",
                      "Remember your login",
                      "Analyze website traffic",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mt: 2 }}
                  >
                    You can disable cookies in your browser settings, but some
                    features may not work properly.
                  </Typography>
                </PolicySection>

                {/* Your Rights */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Shield sx={{ color: "#10b981", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      6. Your Rights
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 2 }}
                  >
                    You have the right to:
                  </Typography>

                  <List>
                    {[
                      "Access your stored data",
                      "Request corrections or updates",
                      "Request deletion (subject to legal/business constraints)",
                      "Opt out of promotional communications",
                    ].map((item, index) => (
                      <ListItem key={index} sx={{ py: 0.5, px: 0 }}>
                        <ListItemText
                          primary={`• ${item}`}
                          sx={{
                            "& .MuiListItemText-primary": {
                              color: "#64748b",
                              lineHeight: 1.6,
                            },
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mt: 2 }}
                  >
                    For any such requests, write to us at info@aequitous.com
                  </Typography>
                </PolicySection>

                {/* Data Retention */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Update sx={{ color: "#f59e0b", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      7. Data Retention
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8 }}
                  >
                    We retain your data for as long as your account is active
                    and for a reasonable period afterward for compliance and
                    audit purposes.
                  </Typography>
                </PolicySection>

                {/* Third-Party Links */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Business sx={{ color: "#8b5cf6", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      8. Third-Party Links
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8 }}
                  >
                    Our platform may contain links to third-party websites or
                    services. We are not responsible for their content or
                    privacy practices.
                  </Typography>
                </PolicySection>

                {/* Changes to Policy */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <Update sx={{ color: "#60a5fa", mr: 2, fontSize: 24 }} />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      9. Changes to This Policy
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8 }}
                  >
                    We may update this Privacy Policy from time to time. We will
                    notify you via email or platform notification. Continued use
                    of our service after updates constitutes your consent to the
                    revised policy.
                  </Typography>
                </PolicySection>

                {/* Contact Information */}
                <PolicySection elevation={0}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 3 }}>
                    <ContactMail
                      sx={{ color: "#60a5fa", mr: 2, fontSize: 24 }}
                    />
                    <Typography variant="h5" fontWeight={700} color="#1a365d">
                      10. Contact Information
                    </Typography>
                  </Box>

                  <Typography
                    variant="body1"
                    color="#64748b"
                    sx={{ lineHeight: 1.8, mb: 3 }}
                  >
                    If you have any questions about this Privacy Policy or our
                    data practices, please contact us:
                  </Typography>

                  <Box
                    sx={{
                      p: 3,
                      borderRadius: 2,
                      backgroundColor: "#f8fafc",
                      border: "1px solid #e2e8f0",
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      color="#1a365d"
                      gutterBottom
                    >
                      Aequitous Services Pvt. Ltd. (formerly Yuvajunction
                      Services Pvt. Ltd.)
                    </Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
                      <strong>Email:</strong> info@aequitous.com
                    </Typography>
                    <Typography variant="body2" color="#64748b" sx={{ mb: 1 }}>
                      <strong>Phone:</strong> +91 22 4515 2489
                    </Typography>
                    <Typography variant="body2" color="#64748b">
                      <strong>Address:</strong> Mumbai, Maharashtra, India
                    </Typography>
                  </Box>
                </PolicySection>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Section>

      {/* Footer */}
      <Box
        component="footer"
        sx={{
          backgroundColor: "#0f172a",
          color: "#e2e8f0",
          py: 4,
          textAlign: "center",
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" sx={{ color: "#94a3b8" }}>
            © {new Date().getFullYear()} Aequitous Pvt. Ltd. All rights
            reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
